/**
 * Importación Histórica de DTEs.
 *
 * Tab 1 (Nueva carga): formulario simple basado en path del servidor (futuro:
 * upload zip + multipart). Permite arrancar una corrida dry-run o real.
 *
 * Tab 2 (Historial): listado de runs con conteos, status, errores y opción
 * de rollback.
 */
import { useState } from 'react'
import { Loader2, Play, RefreshCw, AlertCircle, Trash2, FileText, UploadCloud, CheckCircle2 } from 'lucide-react'
import { useHistoricalRuns, useHistoricalRunDetail, useCreateRun, useRollbackRun, useUploadXmls, type HistoricalImportRun, type RunFlow, type ProductPolicy, type UploadResult } from '@/modules/historical_import/hooks/useHistoricalImport'
import { useConfirmModal, ConfirmModal } from '@/components/ConfirmModal'
import { DataTable, type DataTableColumn } from '@/components/ds/DataTable'
import { Pagination } from '@/components/ds/Pagination'
import { EmptyState } from '@/components/EmptyState'
import { useTenantLocale } from '@/lib/locale'
import { Modal } from '@/components/ds/Modal'

const FLOW_LABEL: Record<RunFlow, string> = { sales: 'Ventas', purchases: 'Compras' }
const POLICY_LABEL: Record<ProductPolicy, string> = {
  generic: 'Genérico (1 producto __HIST__)',
  per_sku: 'Crear producto por SKU',
  hybrid: 'Híbrido (matchea SKU, huérfanos al genérico)',
}
const STATUS_LABEL: Record<string, string> = {
  queued: 'En espera', running: 'En ejecución', completed: 'Completada', failed: 'Fallida', aborted: 'Abortada',
}
const STATUS_CLS: Record<string, string> = {
  queued: 'bg-glass-3 text-content-muted',
  running: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  completed: 'bg-success-surface-strong text-success-content-strong',
  failed: 'bg-danger-surface-strong text-danger-content-strong',
  aborted: 'bg-warning-surface-strong text-warning-content-strong',
}

function NewRunForm() {
  const [flow, setFlow] = useState<RunFlow>('purchases')
  const [policy, setPolicy] = useState<ProductPolicy>('hybrid')
  const [dryRun, setDryRun] = useState(true)
  const [upload, setUpload] = useState<UploadResult | null>(null)
  const [uploadPct, setUploadPct] = useState(0)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useState<HTMLInputElement | null>(null)
  const createRun = useCreateRun()
  const uploadMut = useUploadXmls()

  const handleFiles = async (files: FileList | File[]) => {
    const arr = Array.from(files)
    if (arr.length === 0) return
    setUploadPct(0)

    const zip = arr.find(f => f.name.toLowerCase().endsWith('.zip'))
    const xmls = arr.filter(f => f.name.toLowerCase().endsWith('.xml'))

    try {
      const result = await uploadMut.mutateAsync({
        file: zip,
        files: zip ? undefined : xmls,
        onProgress: setUploadPct,
      })
      setUpload(result)
    } catch {
      // error queda en uploadMut.error
    }
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!upload) return
    await createRun.mutateAsync({
      flow,
      upload_id: upload.upload_id,
      product_policy: policy,
      dry_run: dryRun,
    })
    setUpload(null)
    setUploadPct(0)
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files)
  }

  return (
    <form onSubmit={submit} className="bg-glass backdrop-blur-glass border border-divider rounded-card p-5 space-y-4 shadow-card">
      <h3 className="text-sm font-semibold text-content">Nueva corrida</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs text-content-muted">Flujo</span>
          <select
            value={flow}
            onChange={e => setFlow(e.target.value as RunFlow)}
            className="mt-1 block w-full rounded-lg border border-divider bg-canvas px-3 py-2 text-sm text-content"
          >
            <option value="purchases">Compras</option>
            <option value="sales">Ventas</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-content-muted">Política de productos</span>
          <select
            value={policy}
            onChange={e => setPolicy(e.target.value as ProductPolicy)}
            className="mt-1 block w-full rounded-lg border border-divider bg-canvas px-3 py-2 text-sm text-content"
          >
            <option value="hybrid">{POLICY_LABEL.hybrid}</option>
            <option value="generic">{POLICY_LABEL.generic}</option>
            <option value="per_sku">{POLICY_LABEL.per_sku}</option>
          </select>
        </label>
      </div>

      {/* Dropzone */}
      {!upload && (
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef[0]?.click()}
          className={`block border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-primary bg-primary-50 dark:bg-primary-900/20' : 'border-divider hover:bg-soft'
          }`}
        >
          <input
            ref={el => fileInputRef[1](el)}
            type="file"
            accept=".zip,.xml"
            multiple
            className="hidden"
            onChange={e => e.target.files && handleFiles(e.target.files)}
          />
          {uploadMut.isPending ? (
            <div className="space-y-2">
              <Loader2 size={20} className="mx-auto animate-spin text-primary" />
              <p className="text-sm text-content">Subiendo… {uploadPct}%</p>
              <div className="w-full bg-divider rounded-full h-1.5 overflow-hidden">
                <div className="bg-primary h-full transition-all" style={{ width: `${uploadPct}%` }} />
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              <UploadCloud size={28} className="mx-auto text-content-muted" />
              <p className="text-sm text-content">
                Arrastra un <b>.zip</b> con los XMLs o haz clic para seleccionar
              </p>
              <p className="text-xs text-content-subtle">
                También puedes subir XMLs sueltos. Máximo 100 MB / 10,000 archivos.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Upload listo */}
      {upload && (
        <div className="bg-success-surface border border-success-border rounded-lg p-3 flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={16} className="text-success-content-soft" />
            <span className="text-content">
              <b>{upload.file_count}</b> XMLs subidos ({upload.total_size_kb} KB)
              {upload.warnings.length > 0 && (
                <span className="text-warning-content ml-2">
                  · {upload.warnings.length} advertencias
                </span>
              )}
            </span>
          </div>
          <button
            type="button"
            onClick={() => { setUpload(null); setUploadPct(0) }}
            className="text-xs text-content-muted hover:text-content"
          >
            Cambiar archivo
          </button>
        </div>
      )}

      {uploadMut.isError && (
        <div className="text-xs text-danger-content-soft flex items-center gap-1">
          <AlertCircle size={14} />
          {(uploadMut.error as { response?: { data?: { error?: { message?: string; details?: Record<string, string[]> } } } })?.response?.data?.error?.message
            || 'Error al subir el archivo.'}
        </div>
      )}

      <label className="flex items-center gap-2 text-sm text-content">
        <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} />
        Dry-run (solo validar, no persistir)
      </label>
      <div className="flex items-center justify-end gap-3">
        {createRun.isError && (
          <span className="text-xs text-danger-content-soft flex items-center gap-1">
            <AlertCircle size={14} />
            {(createRun.error as Error).message}
          </span>
        )}
        <button
          type="submit"
          disabled={createRun.isPending || !upload}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary rounded-lg text-sm font-medium hover:bg-primary-hover disabled:opacity-50"
        >
          {createRun.isPending ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          {dryRun ? 'Ejecutar dry-run' : 'Ejecutar carga'}
        </button>
      </div>
    </form>
  )
}

/**
 * Las columnas del historial de corridas.
 *
 * 🔴 Esto era `RunRow`, un componente que emitía su propio `<tr>` — uno de los
 * dos «componente-fila» que quedaron fuera del barrido anterior. Es render
 * puro (no consulta ni guarda nada suyo), así que se desarma en columnas sin
 * perder nada.
 *
 * ⚠️ **Ninguna columna es `sortable` a propósito**: este listado lo pagina el
 * servidor (`runsQ.data.pages`), y ordenar en el cliente ordenaría solo la
 * página visible — un orden que miente, que es justo lo que advierte
 * `DataTableColumn.sortable`.
 */
function columnasDeCorrida({ onSelect, onRollback }: {
  onSelect: (r: HistoricalImportRun) => void
  onRollback: (r: HistoricalImportRun) => void
}): DataTableColumn<HistoricalImportRun>[] {
  return [
    { key: 'started_at', header: 'Fecha',
      render: run => (
        <span className="font-mono">{new Date(run.started_at).toLocaleString('es-CL')}</span>
      ) },
    { key: 'flow', header: 'Flujo',
      render: run => (
        <>
          <span className={`inline-block px-2 py-0.5 rounded ${run.flow === 'sales' ? 'bg-warning-surface-strong text-warning-content-strong' : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'}`}>
            {FLOW_LABEL[run.flow]}
          </span>
          {run.dry_run && <span className="ml-2 text-content-subtle">(dry-run)</span>}
        </>
      ) },
    { key: 'docs_created', header: 'Creados', numeric: true, render: run => run.docs_created },
    { key: 'docs_skipped', header: 'Skipped', numeric: true,
      render: run => <span className="text-content-muted">{run.docs_skipped}</span> },
    { key: 'errors_count', header: 'Errores', numeric: true,
      render: run => run.errors_count > 0
        ? <span className="text-danger-content-soft font-medium">{run.errors_count}</span>
        : <span className="text-content-muted">0</span> },
    { key: 'status', header: 'Estado',
      render: run => (
        <span className={`inline-block px-2 py-0.5 rounded-full ${STATUS_CLS[run.status] || ''}`}>
          {STATUS_LABEL[run.status] || run.status}
        </span>
      ) },
    { key: 'duration_seconds', header: 'Duración', numeric: true,
      render: run => (
        <span className="text-content-muted">
          {run.duration_seconds != null ? `${run.duration_seconds}s` : '—'}
        </span>
      ) },
    { key: 'started_by_email', header: 'Usuario',
      render: run => <span className="text-content-muted">{run.started_by_email || '—'}</span> },
    { key: 'acciones', header: <span className="sr-only">Acciones</span>,
      render: run => (
        <div className="text-right whitespace-nowrap">
          <button
            onClick={() => onSelect(run)}
            className="text-xs text-primary hover:underline mr-3"
            title="Ver detalle"
          >
            Ver
          </button>
          {!run.dry_run && run.status !== 'running' && run.docs_created > 0 && (
            <button
              onClick={() => onRollback(run)}
              className="text-xs text-danger-content-soft hover:underline inline-flex items-center gap-1"
              title="Eliminar todo lo creado por esta corrida"
            >
              <Trash2 size={12} /> Rollback
            </button>
          )}
        </div>
      ) },
  ]
}

function RunDetailModal({ runId, onClose }: { runId: string; onClose: () => void }) {
  const { data, isLoading } = useHistoricalRunDetail(runId)
  return (
    <Modal open onClose={onClose} title="Detalle de corrida" size="2xl">
      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading || !data ? (
          <div className="flex items-center gap-2 text-sm text-content-muted">
            <Loader2 size={14} className="animate-spin" /> Cargando…
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Field label="ID" value={<span className="font-mono text-xs">{data.id}</span>} />
              <Field label="Status" value={
                <span className={`inline-block px-2 py-0.5 rounded-full ${STATUS_CLS[data.status] || ''}`}>
                  {STATUS_LABEL[data.status] || data.status}
                </span>
              } />
              <Field label="Flujo" value={FLOW_LABEL[data.flow]} />
              <Field label="País" value={data.country} />
              <Field label="Dry-run" value={data.dry_run ? 'Sí' : 'No'} />
              <Field label="Política productos" value={POLICY_LABEL[data.product_policy] || data.product_policy} />
              <Field label="Path" value={<span className="font-mono text-xs break-all">{data.source_path}</span>} />
              <Field label="Iniciado por" value={data.started_by_email || '—'} />
              <Field label="Inicio" value={new Date(data.started_at).toLocaleString('es-CL')} />
              <Field label="Fin" value={data.finished_at ? new Date(data.finished_at).toLocaleString('es-CL') : '—'} />
              <Field label="Duración" value={data.duration_seconds != null ? `${data.duration_seconds}s` : '—'} />
              <Field label="Archivos" value={data.total_files} />
            </div>

            <div className="grid grid-cols-3 gap-3 pt-3 border-t border-divider">
              <Counter label="Docs creados" value={data.docs_created} color="text-success-content" />
              <Counter label="Docs ya existían" value={data.docs_skipped} color="text-content-muted" />
              <Counter label="Errores" value={data.errors_count} color={data.errors_count ? 'text-danger-content' : 'text-content-muted'} />
              <Counter label="Customers creados" value={data.customers_created} />
              <Counter label="Suppliers creados" value={data.suppliers_created} />
              <Counter label="Productos creados" value={data.products_created} />
            </div>

            {data.errors_count > 0 && (
              <div className="pt-3 border-t border-divider">
                <h4 className="text-xs font-semibold text-content mb-2 flex items-center gap-2">
                  <AlertCircle size={14} className="text-danger-content-soft" /> Errores
                </h4>
                <div className="space-y-1 max-h-60 overflow-y-auto text-xs font-mono">
                  {data.errors_json.slice(0, 50).map((e, i) => (
                    <div key={i} className="text-content-muted">
                      {e.file ? `${e.file} ` : ''}{e.folio != null ? `#${e.folio} ` : ''}— {e.error}
                    </div>
                  ))}
                  {data.errors_json.length > 50 && (
                    <div className="text-content-subtle">
                      ... y {data.errors_json.length - 50} errores más.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-content-muted">{label}</div>
      <div className="text-sm text-content">{value}</div>
    </div>
  )
}

function Counter({ label, value, color = 'text-content' }: { label: string; value: number; color?: string }) {
  return (
    <div className="bg-canvas border border-divider rounded-lg p-3">
      <div className="text-xs text-content-muted">{label}</div>
      <div className={`text-xl font-semibold ${color}`}>{value}</div>
    </div>
  )
}

export function HistoricalImportPage() {
  const locale = useTenantLocale()
  const [page, setPage] = useState(1)
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const runsQ = useHistoricalRuns({ page, pageSize: 25 })
  const rollback = useRollbackRun()
  const { confirm, modalProps } = useConfirmModal()

  const handleRollback = (run: HistoricalImportRun) => {
    confirm({
      message: `¿Eliminar los ${run.docs_created} documentos creados por la corrida del ${new Date(run.started_at).toLocaleString('es-CL')}? Los XMLs en S3 quedan archivados.`,
      variant: 'danger',
      confirmLabel: 'Rollback definitivo',
      onConfirm: async () => {
        await rollback.mutateAsync(run.id)
      },
    })
  }

  return (
    <div className="space-y-6">
      <NewRunForm />

      <div className="bg-glass backdrop-blur-glass border border-divider rounded-card overflow-hidden shadow-card">
        <div className="flex items-center justify-between px-5 py-3 border-b border-divider">
          <h3 className="text-sm font-semibold text-content flex items-center gap-2">
            <FileText size={16} /> Historial de corridas
          </h3>
          <button
            onClick={() => runsQ.refetch()}
            className="text-xs text-content-muted hover:text-content inline-flex items-center gap-1"
          >
            <RefreshCw size={12} className={runsQ.isFetching ? 'animate-spin' : ''} /> Refrescar
          </button>
        </div>
        <DataTable
          columns={columnasDeCorrida({
            onSelect: r => setSelectedRunId(r.id),
            onRollback: handleRollback,
          })}
          rows={runsQ.data?.runs ?? []}
          rowKey={run => run.id}
          loading={runsQ.isLoading}
          loadingNode={
            <div className="px-3 py-6 text-center text-content-muted">
              <Loader2 size={14} className="animate-spin inline-block mr-2" /> Cargando…
            </div>
          }
          empty={
            <EmptyState
              title="Sin corridas aún"
              hint="Inicia una nueva arriba."
              size="sm"
            />
          }
          minWidth={900}
          surface={false}
        />
        {runsQ.data && runsQ.data.pages > 1 && (
            <Pagination
              page={page}
              pageSize={runsQ.data.page_size}
              total={runsQ.data.count}
              onPageChange={setPage}
              locale={locale}
            />
          )}
      </div>

      {selectedRunId && (
        <RunDetailModal runId={selectedRunId} onClose={() => setSelectedRunId(null)} />
      )}
      <ConfirmModal {...modalProps} />
    </div>
  )
}
